import {
  Avatar,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material'
import { FC, useMemo } from 'react'
import { Icons } from 'utoniq-ui'

import { formatNumber } from '@/clients/utoniq-core'
import {
  PendingPurchaseItemDetailFragment,
  PendingPurchaseItemSkuDetailFragment,
  PendingPurchaseItemStatus,
  SkuUnit,
  UserPaymentType,
} from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, Organism, OrganismFrame } from '@/core/architecture'
import { getActiveMembershipSubscriptionProductPlan } from '@/core/membership'

type PurchaseItemProps = {
  clickable?: boolean
  pendingPurchaseItem: PendingPurchaseItemDetailFragment
}
export const PurchaseItem: Organism<PurchaseItemProps> = ({
  clickable = true,
  pendingPurchaseItem,
}) => {
  const { t } = useTranslation('paymentStatuses')
  const { generatePath, routerPages } = useRouter()

  const paymentType = useMemo<UserPaymentType>(() => {
    return pendingPurchaseItem.items?.some(
      i => i.item.paymentType === UserPaymentType.Cash
    )
      ? UserPaymentType.Cash
      : UserPaymentType.PrepaidPoint
  }, [pendingPurchaseItem.items])

  const product = useMemo(() => {
    return getActiveMembershipSubscriptionProductPlan(
      pendingPurchaseItem?.artist,
      pendingPurchaseItem.metadata?.membership?.planId ?? ''
    )
  }, [
    pendingPurchaseItem?.artist,
    pendingPurchaseItem.metadata?.membership?.planId,
  ])

  const price = useMemo(() => {
    if (product) {
      return product.plan.amount
    }
    return (
      pendingPurchaseItem.totalPrice +
      (pendingPurchaseItem.checkoutFees?.totalFee ?? 0)
    )
  }, [
    pendingPurchaseItem.checkoutFees?.totalFee,
    pendingPurchaseItem.totalPrice,
    product,
  ])

  return (
    <OrganismFrame>
      <Card elevation={0} sx={{ borderRadius: 2 }}>
        <CardActionArea
          disabled={
            !clickable ||
            pendingPurchaseItem.status === PendingPurchaseItemStatus.Failed
          }
          href={generatePath(routerPages.PaymentStatusDetail, {
            pendingPurchaseId: pendingPurchaseItem.id,
            itemId: pendingPurchaseItem?.items?.[0]?.itemId ?? '#',
          })}
        >
          <CardContent
            sx={{
              p: 5,
              '&:last-child': { pb: 5 },
              '*': {
                color: theme =>
                  pendingPurchaseItem.status ===
                  PendingPurchaseItemStatus.Failed
                    ? `${theme.palette.text.disabled}!important`
                    : undefined,
              },
            }}
          >
            <Stack
              direction="row"
              spacing={4}
              sx={{ pb: 2 }}
              alignItems="center"
            >
              <SettlementTag status={pendingPurchaseItem.status} />
              <Typography
                variant="subtitle2"
                color={
                  pendingPurchaseItem.status ===
                  PendingPurchaseItemStatus.WaitingPayment
                    ? 'primary.main'
                    : 'text.disabled'
                }
              >
                {t('paymentDeadline')}：{pendingPurchaseItem.formattedExpiredAt}
              </Typography>
            </Stack>
            <Stack spacing={2}>
              {product ? (
                <Stack direction="row" alignItems="center" spacing={2} py={1}>
                  <Stack flex={1} spacing={1}>
                    <Typography variant="subtitle1" color="text.secondary">
                      {t('orderDetails')}
                    </Typography>
                    <Typography variant="subtitle1">{product.title}</Typography>
                  </Stack>
                  <Avatar
                    src={pendingPurchaseItem.artist?.avatar?.smallUri ?? ''}
                    sx={{ width: 48, height: 48 }}
                  />
                </Stack>
              ) : (
                <Stack>
                  {paymentType === UserPaymentType.Cash && (
                    <>
                      <Stack spacing={1}>
                        <Typography variant="subtitle1" color="text.secondary">
                          {t('orderDetails')}
                        </Typography>
                        <Typography variant="subtitle1">
                          {pendingPurchaseItem.items?.map(item => {
                            if (item.skus.length) {
                              return item.skus.map(sku => (
                                <ItemDetailCell
                                  key={sku.skuId}
                                  item={item.item}
                                  sku={sku.sku}
                                  count={sku.count}
                                />
                              ))
                            }
                            return (
                              <ItemDetailCell
                                key={item.itemId}
                                item={item.item}
                                count={item.count}
                              />
                            )
                          })}
                        </Typography>
                      </Stack>
                    </>
                  )}
                  {paymentType === UserPaymentType.PrepaidPoint && (
                    <Stack flex={1} direction="row">
                      <Stack flex={1} spacing={1}>
                        <Typography variant="subtitle1" color="text.secondary">
                          {t('orderDetails')}
                        </Typography>
                        <Typography variant="subtitle1">
                          {t('purchasePoint', {
                            point: formatNumber(
                              pendingPurchaseItem.totalPrice +
                                (pendingPurchaseItem.checkoutFees?.totalFee ??
                                  0)
                            ),
                          })}
                        </Typography>
                      </Stack>
                      <Stack justifyContent="center" alignItems="center">
                        <Icons.Point sx={{ fontSize: '48px' }} />
                        <Typography
                          align="center"
                          variant="subtitle2"
                          sx={{
                            width: 56,
                            borderRadius: '100px',
                            bgcolor: 'grey.100',
                            mt: -2,
                          }}
                        >
                          ×
                          {formatNumber(
                            pendingPurchaseItem.totalPrice +
                              (pendingPurchaseItem.checkoutFees?.totalFee ?? 0)
                          )}
                        </Typography>
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              )}
              <Stack spacing={1}>
                <Typography variant="subtitle1" color="text.secondary">
                  {t('payment', '支払金額')}
                </Typography>
                <Typography variant="subtitle1">
                  {t('price', { price: formatNumber(price) })}
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
    </OrganismFrame>
  )
}

type ItemDetailCellProps = {
  item: PendingPurchaseItemSkuDetailFragment['item']
  sku?: Partial<SkuUnit>
  count: number
}
const ItemDetailCell: Cell<ItemDetailCellProps> = ({ item, sku, count }) => {
  const { t } = useTranslation('paymentStatuses')

  if (!item) return null

  return (
    <Stack direction="row" flex={1} spacing={2} justifyContent="space-between">
      <Stack flex={1}>
        <Typography variant="subtitle1" sx={styleSets.lineCamp(2)}>
          {item.title} {!!sku?.unitName && <>({sku.unitName})</>}
        </Typography>
      </Stack>
      <Typography variant="subtitle1">
        {t('itemQuantity', '数量', { quantity: count })}
      </Typography>
    </Stack>
  )
}

type SettlementTagProps = {
  status: PendingPurchaseItemStatus
}
export const SettlementTag: FC<SettlementTagProps> = ({ status }) => {
  const { t } = useTranslation('paymentStatuses')
  const { color, bgcolor, variant, borderColor } = useMemo<{
    color: string
    bgcolor: string
    variant: 'outlined' | 'filled'
    borderColor?: string
  }>(() => {
    switch (status) {
      case PendingPurchaseItemStatus.Completed:
        return {
          color: 'text.disabled',
          bgcolor: 'grey.50',
          variant: 'outlined',
          borderColor: 'text.disabled',
        }
      case PendingPurchaseItemStatus.WaitingPayment:
        return {
          color: 'white',
          bgcolor: 'primary.main',
          variant: 'filled',
          borderColor: 'primary.main',
        }
      default:
        return {
          color: 'text.secondary',
          bgcolor: 'grey.100',
          variant: 'filled',
          borderColor: 'grey.100',
        }
    }
  }, [status])
  return (
    <Chip
      label={
        <Typography variant="subtitle2" color={color}>
          {t(status)}
        </Typography>
      }
      variant="filled"
      sx={{
        bgcolor,
        px: 1,
        py: '2px',
        height: 'auto',
        borderRadius: '2px',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor,
        '.MuiChip-label': {
          px: 0,
        },
      }}
    />
  )
}
